import React, { useState, useEffect, useCallback } from "react";
import { Form, FormStructure, FormElement } from "../../types";
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

interface FormEditorProps {
  form?: Form | null;
  onSave: (form: Partial<Form>) => void;
  onCancel: () => void;
  readonly?: boolean;
  showLibraryButton?: boolean;
  onShowLibrary?: () => void;
}

// AVAILABLE_FORM_ELEMENTS will be created dynamically using translations

const FormEditor: React.FC<FormEditorProps> = ({
  form,
  onSave,
  onCancel,
  readonly = false,
  showLibraryButton = false,
  onShowLibrary,
}) => {
  const { t } = useTranslation(["forms", "common"]);

  // Create AVAILABLE_FORM_ELEMENTS dynamically with translations
  const AVAILABLE_FORM_ELEMENTS: Omit<FormElement, "id">[] = React.useMemo(
    () => [
      {
        type: "text",
        label: t("forms:elementLabels.textInput"),
        required: false,
        placeholder: t("forms:elementPlaceholders.enterText"),
      },
      {
        type: "textarea",
        label: t("forms:elementLabels.textarea"),
        required: false,
        placeholder: t("forms:elementPlaceholders.writeDescription"),
      },
      {
        type: "number",
        label: t("forms:elementLabels.numberInput"),
        required: false,
        placeholder: t("forms:elementPlaceholders.enterNumber"),
      },
      {
        type: "date",
        label: t("forms:elementLabels.datePicker"),
        required: false,
      },
      {
        type: "select",
        label: t("forms:elementLabels.selectList"),
        required: false,
        options: [t("forms:option1"), t("forms:option2"), t("forms:option3")],
      },
      {
        type: "checkbox",
        label: t("forms:elementLabels.checkbox"),
        required: false,
      },
    ],
    [t]
  );

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isPublic: true,
  });

  const [formStructure, setFormStructure] = useState<FormStructure>({
    elements: [],
    elementOrder: [],
    settings: {
      theme: "default",
      validation: {
        showErrors: true,
        validateOnBlur: false,
        validateOnSubmit: true,
      },
    },
  });

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submissionData, setSubmissionData] = useState<Record<string, any>>({});
  const supportsMultipleSelection = useCallback(
    (element: FormElement) => element.type === "select",
    [],
  );
  const isMultipleSelectionEnabled = useCallback(
    (element: FormElement) =>
      supportsMultipleSelection(element) && Boolean(element.allowMultiple),
    [supportsMultipleSelection],
  );

  useEffect(() => {
    if (form) {
      setFormData({
        title: form.title || "",
        description: form.description || "",
        isPublic: form.isPublic !== false,
      });

      if (form.formStructure) {
        setFormStructure(form.formStructure);
      }

      if (form.submissionData) {
        setSubmissionData(form.submissionData);
      }
    }
  }, [form]);

  const generateId = () =>
    `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addElement = useCallback((elementType: Omit<FormElement, "id">) => {
    const newElement: FormElement = {
      ...elementType,
      id: generateId(),
    };

    setFormStructure((prev) => ({
      ...prev,
      elements: [...prev.elements, newElement],
      elementOrder: [...prev.elementOrder, newElement.id],
    }));
  }, []);

  const removeElement = useCallback((elementId: string) => {
    setFormStructure((prev) => ({
      ...prev,
      elements: prev.elements.filter((el) => el.id !== elementId),
      elementOrder: prev.elementOrder.filter((id) => id !== elementId),
    }));
  }, []);

  const moveElement = useCallback((fromIndex: number, toIndex: number) => {
    setFormStructure((prev) => {
      const newOrder = [...prev.elementOrder];
      const [movedId] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedId);
      return {
        ...prev,
        elementOrder: newOrder,
      };
    });
  }, []);

  const updateElement = useCallback(
    (elementId: string, updates: Partial<FormElement>) => {
      setFormStructure((prev) => ({
        ...prev,
        elements: prev.elements.map((el) =>
          el.id === elementId ? { ...el, ...updates } : el
        ),
      }));
    },
    []
  );

  const updateSubmissionData = useCallback((fieldId: string, value: any) => {
    setSubmissionData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error(t("forms:formTitleRequired"));
      return;
    }

    if (formStructure.elements.length === 0) {
      toast.error(t("forms:atLeastOneElementRequired"));
      return;
    }

    setLoading(true);

    try {
      const formToSave: Partial<Form> = {
        ...form,
        ...formData,
        formStructure,
        submissionData: {}, // Manager cannot enter values, form will be filled by employees
      };

      onSave(formToSave);
    } catch (error) {
      toast.error(t("forms:errorSavingForm"));
    } finally {
      setLoading(false);
    }
  };

  const renderFormElement = (element: FormElement, index: number) => {
    const value = submissionData[element.id] || element.value || "";

    return (
      <div
        key={element.id}
        className="bg-white border rounded-lg p-4 mb-4 shadow-sm"
      >
        {!isPreviewMode && !readonly && (
          <div className="flex justify-between items-center mb-3 pb-2 border-b">
            <div className="flex items-center space-x-2 flex-1">
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                {element.type.toUpperCase()}
              </span>
              <div className="flex items-center space-x-2 flex-1 border border-gray-300 rounded-md px-3 py-1.5 bg-gray-50 hover:bg-white hover:border-blue-400 transition-all">
                <PencilIcon className="w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={element.label}
                  onChange={(e) =>
                    updateElement(element.id, { label: e.target.value })
                  }
                  className="flex-1 text-sm font-medium bg-transparent border-none focus:outline-none"
                  placeholder={t("forms:enterElementName")}
                />
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {index > 0 && (
                <button
                  onClick={() => moveElement(index, index - 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
              )}
              {index < formStructure.elementOrder.length - 1 && (
                <button
                  onClick={() => moveElement(index, index + 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                >
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => removeElement(element.id)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Element Settings */}
        {!isPreviewMode &&
          !readonly &&
          (element.type === "select" || element.type.includes("select")) && (
            <div className="mb-3 p-3 bg-gray-50 rounded-md space-y-3">
              {supportsMultipleSelection(element) && (
                <div className="flex items-center justify-between gap-4 rounded-md border border-blue-100 bg-white p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {t("forms:projectBuilder.selectionMode")}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t(
                        "forms:projectBuilder.selectionModeDescription",
                        "Bu alanda tekli veya çoklu seçim yapılmasını belirleyin.",
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-semibold ${
                        !isMultipleSelectionEnabled(element)
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}
                    >
                      {t("forms:projectBuilder.singleSelection", "Tekli seçim")}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        updateElement(element.id, {
                          allowMultiple: !isMultipleSelectionEnabled(element),
                        })
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isMultipleSelectionEnabled(element)
                          ? "bg-blue-600"
                          : "bg-gray-300"
                      }`}
                      aria-pressed={isMultipleSelectionEnabled(element)}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          isMultipleSelectionEnabled(element)
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                    <span
                      className={`text-xs font-semibold ${
                        isMultipleSelectionEnabled(element)
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}
                    >
                      {t("forms:projectBuilder.multipleSelection", "Çoklu seçim")}
                    </span>
                  </div>
                </div>
              )}
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Seçenekler
              </label>

              {/* Seçenek Listesi */}
              <div className="space-y-2">
                {(element.options || []).map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {/* Input Field */}
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(element.options || [])];
                        newOptions[index] = e.target.value;
                        updateElement(element.id, { options: newOptions });
                      }}
                      placeholder={`Seçenek ${index + 1}`}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    {/* Sil Butonu */}
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = (element.options || []).filter(
                          (_, i) => i !== index
                        );
                        // En az 1 seçenek olmalı
                        if (newOptions.length === 0) {
                          toast.error("En az bir seçenek gereklidir");
                          return;
                        }
                        updateElement(element.id, { options: newOptions });
                      }}
                      disabled={(element.options || []).length <= 1}
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Seçeneği Sil"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Seçenek Ekle Butonu */}
              <button
                type="button"
                onClick={() => {
                  const newOptions = [...(element.options || []), ""];
                  updateElement(element.id, { options: newOptions });
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Seçenek Ekle
              </button>
            </div>
          )}

        {/* Form Element Rendering */}
        {renderFormField(element, value)}
      </div>
    );
  };

  const renderFormField = (element: FormElement, value: any) => {
    switch (element.type) {
      case "text":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUser")}
              </div>
            </div>
          </div>
        );

      case "textarea":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-8">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserMultiline")}
              </div>
            </div>
          </div>
        );

      case "number":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserNumber")}
              </div>
            </div>
          </div>
        );

      case "date":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserDate")}
              </div>
            </div>
          </div>
        );

      case "select":
      case "project_type_select":
      case "progress_method_select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserSelect")}
              </div>
              {element.type === "select" && (
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {isMultipleSelectionEnabled(element)
                    ? t("forms:projectBuilder.multipleSelection", "Çoklu seçim")
                    : t("forms:projectBuilder.singleSelection", "Tekli seçim")}
                </div>
              )}
              {element.options && element.options.length > 0 && (
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {t("forms:options")}: {element.options.join(", ")}
                </div>
              )}
            </div>
          </div>
        );

      case "checkbox":
        return (
          <div>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserCheckbox")}
              </div>
              <div className="mt-1 text-xs text-gray-400 text-center">
                {element.label}
              </div>
            </div>
          </div>
        );

      case "budget_input":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUserBudget")}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:fieldWillBeFilledByUser")}
              </div>
            </div>
          </div>
        );
    }
  };

  const orderedElements = formStructure.elementOrder
    .map((id) => formStructure.elements.find((el) => el.id === id))
    .filter(Boolean) as FormElement[];

  return (
    <>
      {/* Full Screen Modal Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <span className="text-2xl">📝</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {readonly
                      ? t("forms:viewForm")
                      : form
                      ? t("forms:editForm")
                      : t("forms:createNewForm")}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {readonly
                      ? t("forms:viewFormDescription")
                      : form
                      ? t("forms:editFormDescription")
                      : t("forms:createFormDescription")}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {showLibraryButton && onShowLibrary && (
                  <button
                    onClick={onShowLibrary}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <span>📚</span>
                    <span>{t("forms:selectFromLibrary")}</span>
                  </button>
                )}
                {!readonly && (
                  <button
                    onClick={() => setIsPreviewMode(!isPreviewMode)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {isPreviewMode ? (
                      <PencilIcon className="h-4 w-4 mr-2 inline" />
                    ) : (
                      <EyeIcon className="h-4 w-4 mr-2 inline" />
                    )}
                    {isPreviewMode ? t("forms:edit") : t("forms:preview")}
                  </button>
                )}
                <button
                  onClick={onCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Form Basic Info */}
          {!isPreviewMode && (
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("forms:formTitle")} *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    disabled={readonly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder={t("forms:enterFormTitle")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("common:description")}
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    disabled={readonly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder={t("forms:formDescriptionPlaceholder")}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="flex h-full">
              {/* Element Palette */}
              {!isPreviewMode && !readonly && (
                <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("forms:formElements")}
                  </h3>
                  <div className="space-y-2">
                    {AVAILABLE_FORM_ELEMENTS.map((element, index) => (
                      <button
                        key={index}
                        onClick={() => addElement(element)}
                        className="w-full text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200 hover:border-blue-300 group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-800 group-hover:text-blue-800">
                              {element.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {element.type.toUpperCase()}
                            </div>
                          </div>
                          <PlusIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Builder Area */}
              <div className="flex-1 p-6">
                {orderedElements.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <div className="text-6xl mb-4">📝</div>
                    <h3 className="text-xl font-medium mb-2">
                      {t("forms:startCreatingForm")}
                    </h3>
                    <p className="text-sm">
                      {readonly
                        ? t("forms:noElementsInFormYet")
                        : t("forms:addElementsFromLeft")}
                    </p>
                  </div>
                ) : (
                  <div className="max-w-2xl mx-auto">
                    <div className="space-y-4">
                      {orderedElements.map((element, index) =>
                        renderFormElement(element, index)
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          {!readonly && (
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {loading && (
                    <span className="flex items-center text-blue-600 text-sm">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></div>
                      {t("forms:saving")}
                    </span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={onCancel}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t("common:cancel")}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>💾</span>
                    <span>
                      {loading ? t("forms:saving") : t("common:save")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FormEditor;
